import React, {
  useState,
  useCallback,
  CSSProperties,
  useEffect,
  useImperativeHandle,
} from 'react';
import styled from 'astroturf/react';
import useTranslationTest, {
  FixedTFunction,
} from 'js/utils/hooks/useTranslationTest';
import {
  ContentContainer,
  Prompt,
} from '../reg_path/signup_to_download_corex/shared/utils';
import { Icon } from 'js/design_system/Icon';
import routes from 'js/core_pre_webpack/routes';
import { Button } from 'js/design_system/Button';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import { Spinner } from 'js/social/shared/CreateSocialItem/Spinner';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { KiiSource } from './api/KiiDataApi';
import { KiiMatchingComponentHandle } from './KiiComponent';

const TRANSLATIONS_NAMESPACE = 'RegPath.SignupToDownload.PaperClaiming';

type PaperClaimWork = {
  // Displayed to the user
  title: string;
  author_names: string;

  // e.g. "https://openalex.org/W4391876427"
  id: string;

  // e.g. "https://openalex.org/A5112836168"
  inferred_author_id: string;

  // Similarity score of work to SUTD seed
  score: number | null;
};

export type PaperClaimData = {
  // Are our precalculated results ready?
  ready: boolean;

  // Precalculated works (and corresponding authors), in descending score order
  works: PaperClaimWork[];

  // Detailed information about each step of the precalculation
  timings?: object;
};

type SelectedWorks = { [workId: string]: boolean };

const Skip = styled.p`
  @import 'stylesheets/design_system/font.scss';

  @include body__large;
  text-decoration-line: underline;
  cursor: pointer;
`;

const UnfilledWarning = styled.p`
  @import 'stylesheets/design_system/font.scss';

  @include body__large;

  color: var(--text-error, #b60000);
  margin-top: 0;
  display: flex;
  align-items: center;
  column-gap: 8px;
`;

const Loading = styled.div`
  display: flex;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
`;

const StyledImpressionWatcher = styled(ImpressionWatcher)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const AnswerContainer = styled.button<{ selected: boolean }>`
  appearance: none;
  background: none;
  text-align: start;

  display: flex;
  min-height: 72px;
  padding: 16px;
  align-items: center;

  border-radius: var(--border-radius-radius-md);
  border: 1px solid var(--button-secondary-stroke, #cddaef);

  &.selected {
    border-color: #141413;
    background: var(--button-secondary-fill-hover, #e6ecf7);
  }

  &:hover {
    background: var(--button-secondary-fill-hover, #e6ecf7);
    border-color: var(--button-secondary-stroke-hover, #386ac1);
    cursor: pointer;
  }

  &:active {
    background: var(--button-secondary-fill-press, #cddaef);
    border-color: var(--button-secondary-stroke-press, #0645b1);
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 4px;
  flex: 1 0 0;
`;

const PrimaryText = styled.div`
  @import 'stylesheets/design_system/font.scss';
  @include body__medium;

  align-self: stretch;

  color: var(--text-primary, #141413);
`;

const SecondaryText = styled.div`
  @import 'stylesheets/design_system/font.scss';
  @include body__small;

  height: 24px;
  align-self: stretch;

  color: var(--text-secondary, #535366);

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PaperClaimOnePaper = styled.div`
  display: flex;
  padding-bottom: var(--spacing-sm-24, 24px);
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-sm-24, 24px);
  align-self: stretch;

  border-radius: var(--border-radius-radius-md);
`;

const PaperClaimPaperContent = styled.div`
  display: flex;
  padding: var(--spacing-xs-16, 16px) 16px;
  align-items: flex-start;
  gap: var(--spacing-xs-8, 8px);
  align-self: stretch;

  border-radius: var(--border-radius-radius-md);
  background: var(--background-beige-light);
`;

const StyledIcon = styled(Icon)`
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

const OnePaperTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
`;

const OnePaperSecondaryText = styled.div`
  @import 'stylesheets/design_system/font.scss';
  @include body__medium;

  height: 24px;
  align-self: stretch;

  color: var(--text-secondary, #535366);

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ButtonSelectGroup = styled.div`
  display: flex;
  align-items: center;
  align-content: center;
  gap: 16px 12px;
  align-self: stretch;
  flex-wrap: wrap;
`;

const PillGroup = styled.div`
  display: flex;
  align-items: flex-start;
  align-content: flex-start;
  gap: 8px var(--spacing-xs-8, 8px);
  flex: 1 0 0;
  flex-wrap: wrap;
`;

const Pill = styled(Button)`
  @import 'stylesheets/design_system/font.scss';
  @include body__small;

  display: flex;
  height: 39px;
  padding: var(--spacing-xs-8, 8px) var(--spacing-xs-16, 16px);
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: var(--border-radius-radius-round);
  border: 1px solid var(--stroke-light, #e0ded2);

  background: var(--background-white, #fff);
`;

const PillText = styled.div`
  @import 'stylesheets/design_system/font.scss';
  @include body__medium;

  color: var(--text-primary, #141413);
`;

const getPapers = async () => {
  const route = routes.api_v0_knowledge_graph_paper_claiming_path();

  return fetchWithCsrf(route, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });
};

const SinglePaperClaim: React.FC<{
  work: PaperClaimWork;
  selectedWorks: SelectedWorks;
  onSelect: (workId: string, selected: boolean) => void;
  t: FixedTFunction;
  impressionSource: string;
}> = ({ work, selectedWorks, onSelect, t, impressionSource }) => {
  const selected = !!selectedWorks[work.id];
  const freshState = selectedWorks[work.id] == null;
  const yesStyle: CSSProperties | undefined = selected
    ? { borderColor: '#141413' }
    : undefined;
  const noStyle: CSSProperties | undefined =
    !selected && !freshState ? { borderColor: '#141413' } : undefined;

  return (
    <ImpressionWatcher source={`${impressionSource}_single_paper_claim`}>
      <PaperClaimOnePaper>
        <PaperClaimPaperContent>
          <StyledIcon name={'article'} size={24} />
          <OnePaperTextContainer>
            <PrimaryText>{work.title}</PrimaryText>
            <OnePaperSecondaryText>{work.author_names}</OnePaperSecondaryText>
          </OnePaperTextContainer>
        </PaperClaimPaperContent>
        <ButtonSelectGroup>
          <PillGroup>
            <Pill
              onClick={() => onSelect(work.id, true)}
              style={yesStyle}
              secondary={true}
            >
              <PillText>{t('yes')}</PillText>
            </Pill>
            <Pill
              onClick={() => onSelect(work.id, false)}
              style={noStyle}
              secondary={true}
            >
              <PillText>{t('no')}</PillText>
            </Pill>
          </PillGroup>
        </ButtonSelectGroup>
      </PaperClaimOnePaper>
    </ImpressionWatcher>
  );
};

const MultiPaperClaim: React.FC<{
  works: PaperClaimWork[];
  selectedWorks: SelectedWorks;
  onSelect: (workId: string, selected: boolean) => void;
  onSkip: () => void;
  t: FixedTFunction;
  impressionSource: string;
}> = ({ works, selectedWorks, onSelect, onSkip, t, impressionSource }) => {
  return (
    <ImpressionWatcher source={`${impressionSource}_multi_paper_claim`}>
      <Container>
        {works.map((work) => (
          <StyledImpressionWatcher
            key={work.id}
            source={`${impressionSource}_multi_paper_claim_checkbox`}
            extra={{ work_id: work.id }}
          >
            <AnswerContainer
              selected={!!selectedWorks[work.id]}
              onClick={() => onSelect(work.id, !selectedWorks[work.id])}
            >
              <TextContainer>
                <PrimaryText>{work.title}</PrimaryText>
                <SecondaryText>{work.author_names}</SecondaryText>
              </TextContainer>
            </AnswerContainer>
          </StyledImpressionWatcher>
        ))}
        <ImpressionWatcher
          source={`${impressionSource}_multi_paper_claim_skip`}
        >
          <Skip role="button" onClick={onSkip}>
            {t('skip')}
          </Skip>
        </ImpressionWatcher>
      </Container>
    </ImpressionWatcher>
  );
};

type Props = {
  kiiSource: KiiSource;
  label?: string;
  submitOnSelection?: boolean;
  maxWorks?: number;
  canSelectMultiple?: boolean;
  prefixSource?: boolean; // Add kiiSource as a prefix to the sourced of submitData
  onSkip?: () => void;
  onSuccessfulMatch?:
    | ((taggingId: number) => void)
    | ((taggingId: number) => Promise<void>);
  onFailedMatch?: () => void;
  onLoadingFailed?: () => void;
  onSelect?: (selectedWorks: SelectedWorks) => void;
  onLoadingCompleted?: (data: PaperClaimData) => void;
};

// ms between retries
const RETRY_FREQUENCY_MS = 1000;
// ms before deciding that loading has failed
const FALLBACK_TIME_MS = 5000;

function getSelectedCount(selectedWorks: SelectedWorks) {
  return Object.values(selectedWorks).filter(Boolean).length;
}

function isAnyWorkAnswered(selectedWorks: SelectedWorks) {
  return Object.values(selectedWorks).length > 0;
}

const formatPaperData = (
  paperClaimData: PaperClaimData,
  selectedWorks: SelectedWorks,
) => {
  const formattedData = paperClaimData.works.map((work) => ({
    ...work,
    selected: !!selectedWorks[work.id],
    seen: true, // TODO: placeholder
  }));
  return JSON.stringify(formattedData);
};

export const PaperClaiming = React.forwardRef<
  KiiMatchingComponentHandle,
  Props
>(
  (
    {
      kiiSource,
      label,
      submitOnSelection = false,
      maxWorks,
      canSelectMultiple = false,
      prefixSource = false,
      onSkip,
      onSuccessfulMatch,
      onFailedMatch,
      onLoadingFailed,
      onSelect,
      onLoadingCompleted,
    }: Props,
    ref,
  ) => {
    const [t] = useTranslationTest(TRANSLATIONS_NAMESPACE, true);

    const [selectedWorks, setSelectedWorks] = useState<SelectedWorks>({});

    const [showUnfilledWarning, setShowUnfilledWarning] =
      useState<boolean>(false);

    const [paperClaimData, setPaperClaimData] = useState<PaperClaimData>({
      ready: false,
      works: [],
    });

    const isReady = useCallback(() => paperClaimData.ready, [paperClaimData]);

    const isSingle = useCallback(
      () => paperClaimData.works.length === 1,
      [paperClaimData],
    );

    const submit = async () => {
      submitAndProgress(selectedWorks);
    };

    useImperativeHandle(ref, () => ({
      submit: submit,
    }));

    const submitData = useCallback(
      async (
        selectedWorks: SelectedWorks,
      ): Promise<
        { taggingMatched: false } | { taggingMatched: true; taggingId: number }
      > => {
        const source =
          (prefixSource ? kiiSource + '_' : '') +
          (isSingle() ? 'claim_confirm_one' : 'claim_multiple_select');
        const route = routes.api_v0_knowledge_graph_paper_claiming_submit_path({
          skip_topic_verification: true,
          papers: encodeURIComponent(
            formatPaperData(paperClaimData, selectedWorks),
          ),
          selected_count: getSelectedCount(selectedWorks),
          source,
        });

        const resp = await fetchWithCsrf(route, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
        return await resp.json();
      },
      [isSingle, kiiSource, paperClaimData, prefixSource],
    );

    const handleSkip = async () => {
      submitData(selectedWorks).catch((err) => {
        console.error(err);
      });

      onSkip && onSkip();
    };

    const submitAndProgress = useCallback(
      async (selectedWorks: SelectedWorks) => {
        if (!isAnyWorkAnswered(selectedWorks)) {
          setShowUnfilledWarning(true);
        } else {
          setShowUnfilledWarning(false);
          try {
            const response = await submitData(selectedWorks);
            if (response.taggingMatched) {
              onSuccessfulMatch &&
                (await onSuccessfulMatch(response.taggingId));
            } else {
              onFailedMatch && onFailedMatch();
            }
          } catch {
            onFailedMatch && onFailedMatch();
          }
        }
      },
      [onSuccessfulMatch, onFailedMatch, submitData],
    );

    const handleSelect = useCallback(
      (workId: string, selected: boolean) => {
        const newSelectedWorks: SelectedWorks = {
          //  If we wanted multiselect, we merge in the existing selectedWorks.
          ...(canSelectMultiple ? selectedWorks : {}),
          [workId]: selected,
        };

        onSelect && onSelect(newSelectedWorks);

        setSelectedWorks(newSelectedWorks);

        if (submitOnSelection) {
          submitAndProgress(newSelectedWorks);
        }
      },
      [
        onSelect,
        submitAndProgress,
        submitOnSelection,
        canSelectMultiple,
        selectedWorks,
      ],
    );

    useEffect(() => {
      let retries = 0;
      let timeoutRef: ReturnType<typeof setTimeout>;

      const fetchData = async () => {
        const response = await getPapers();
        const data = await response.json();

        function shuffleArray<T>(array: T[]): T[] {
          const shuffled = [...array]; // Create a copy to avoid mutating the original array
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
          }
          return shuffled;
        }

        if (data.ready) {
          // Log the data to the console (for now) to easily see info that's not in the UI
          // eslint-disable-next-line no-console
          console.log(data);
          if (data.works.length === 0) {
            // If we finished looking for possible works but didn't find any, fall back to typeahead
            onLoadingFailed && onLoadingFailed();
          } else {
            const maxDisplayLength = maxWorks ?? data.works.length;
            const paperClaimData = {
              ...data,
              works: shuffleArray(data.works.slice(0, maxDisplayLength)),
            };

            setPaperClaimData(paperClaimData);

            onLoadingCompleted && onLoadingCompleted(paperClaimData);
          }
        } else {
          // Retry if not yet ready
          retries++;
          if (retries * RETRY_FREQUENCY_MS >= FALLBACK_TIME_MS) {
            // If we didn't finish looking for possible works in time, fall back to typeahead
            onLoadingFailed && onLoadingFailed();
          } else {
            timeoutRef = setTimeout(() => fetchData(), RETRY_FREQUENCY_MS);
          }
        }
      };

      fetchData();

      return () => {
        clearTimeout(timeoutRef);
      };
    }, [onLoadingCompleted, onLoadingFailed, maxWorks]);

    return (
      <ContentContainer>
        {showUnfilledWarning && (
          <UnfilledWarning>
            <Icon name="warning" size={25} />
            {isSingle() ? t('skipwarning_alt') : t('skipwarning')}
          </UnfilledWarning>
        )}
        {isReady() && paperClaimData.works.length > 1 && !!label && (
          <Prompt size="lg" bold>
            {label}
          </Prompt>
        )}
        {!isReady() && (
          <ImpressionWatcher source={`${kiiSource}_paper_claim_loading`}>
            <Loading>
              <Spinner />
            </Loading>
          </ImpressionWatcher>
        )}
        {isReady() && isSingle() && (
          <SinglePaperClaim
            work={paperClaimData.works[0]}
            selectedWorks={selectedWorks}
            onSelect={handleSelect}
            t={t}
            impressionSource={kiiSource}
          />
        )}
        {isReady() && !isSingle() && (
          <MultiPaperClaim
            works={paperClaimData.works}
            selectedWorks={selectedWorks}
            onSelect={handleSelect}
            onSkip={handleSkip}
            t={t}
            impressionSource={kiiSource}
          />
        )}
      </ContentContainer>
    );
  },
);
